/**
 * GuideIndex — Main guide listing page showing all available user guides.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import {
  BookOpen, FileText, BarChart3, FileBadge, ClipboardList, FileQuestion,
  ShoppingCart, Box, Receipt, Boxes, PackageCheck, CalendarClock, BarChart2
} from 'lucide-react';

const GUIDES = [
  { id: 'general', label: 'General System Guide', desc: 'Overview of the Eyvo platform, navigation, dashboard, and core concepts', icon: BookOpen, color: 'bg-slate-100 text-slate-700' },
  { id: 'data-files', label: 'Data Files', desc: 'Master data management: suppliers, items, currencies, delivery points, and more', icon: FileText, color: 'bg-blue-50 text-blue-700' },
  { id: 'account-codes', label: 'Account Codes', desc: 'Expenditure code structure, segments, and budget allocation', icon: BarChart3, color: 'bg-violet-50 text-violet-700' },
  { id: 'contracts', label: 'Contracts', desc: 'Supplier contract management, clauses, workflow, and renewal tracking', icon: FileBadge, color: 'bg-emerald-50 text-emerald-700' },
  { id: 'requisitions', label: 'Requisitions', desc: 'Purchase requisition creation, line items, approvals, and conversion to POs', icon: ClipboardList, color: 'bg-amber-50 text-amber-700' },
  { id: 'rfx-sourcing', label: 'RFx Sourcing', desc: 'Competitive sourcing: RFQ, RFP, RFI, reverse auctions, and award decisions', icon: FileQuestion, color: 'bg-purple-50 text-purple-700' },
  { id: 'purchase-orders', label: 'Purchase Orders', desc: 'PO creation, amendments, approvals, dispatch, and supplier communication', icon: ShoppingCart, color: 'bg-green-50 text-green-700' },
  { id: 'asset-register', label: 'Asset Register', desc: 'Fixed asset tracking, depreciation, disposal, and lifecycle management', icon: Box, color: 'bg-cyan-50 text-cyan-700' },
  { id: 'invoice-matching', label: 'Invoice Matching', desc: 'Three-way matching, auto-matching, email ingestion, and payment tracking', icon: Receipt, color: 'bg-red-50 text-red-700' },
  { id: 'inventory', label: 'Inventory Control', desc: 'Stock management, batch/serial tracking, cycle counting, and replenishment', icon: Boxes, color: 'bg-indigo-50 text-indigo-700' },
  { id: 'goods-receiving', label: 'Receiving', desc: 'GRN creation, quantity verification, delivery notes, and PO linking', icon: PackageCheck, color: 'bg-teal-50 text-teal-700' },
  { id: 'reports', label: 'Reports & Scheduled Reports', desc: 'Reporting system overview, PDF/CSV export, and automated scheduled delivery', icon: CalendarClock, color: 'bg-orange-50 text-orange-700' },
];

export default function GuideIndex() {
  const navigate = useNavigate();

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1e3a5f' }}>
            <BookOpen className="h-6 w-6 inline mr-2 text-primary" />
            Eyvo User Guides
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Comprehensive documentation for every module in Eyvo eProcurement. Click a guide to open it.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {GUIDES.map(g => (
            <button
              key={g.id}
              onClick={() => navigate(`/guides/${g.id}`)}
              className="flex items-start gap-3 p-4 border rounded-xl text-left hover:bg-accent/20 hover:border-primary/30 transition-all group"
              data-testid={`guide-card-${g.id}`}
            >
              <div className={`p-2.5 rounded-lg ${g.color} flex-shrink-0`}>
                <g.icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold group-hover:text-primary transition-colors">{g.label}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{g.desc}</p>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-8 pt-4 border-t text-center">
          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} Eyvo Inc. All rights reserved. &mdash; <a href="https://www.eyvo.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.eyvo.com</a>
          </p>
        </div>
      </div>
    </AppShell>
  );
}
